// src/web/ui.js
//
// UI bindings — a centered, scrollable product carousel plus status/overlay
// helpers.
//
// Carousel behavior:
//   - Horizontal scroll, smaller thumbnails, scroll-snap to center.
//   - Clicking a thumbnail selects it AND animates it to the center.
//   - Padding spacers on both ends let the first/last item reach the centre.

export function buildPicker({ catalog, activeSku, onSelect }) {
  const root = document.getElementById('picker');
  root.innerHTML = '';

  // Leading spacer so the first item can center.
  const leadSpacer = document.createElement('div');
  leadSpacer.className = 'carousel-spacer';
  root.appendChild(leadSpacer);

  const thumbs = [];
  catalog.forEach((item) => {
    const el = document.createElement('button');
    el.className = 'thumb' + (item.sku === activeSku ? ' active' : '');
    el.dataset.sku = item.sku;
    el.title = item.name;
    el.innerHTML = `
      <img src="${item.thumbnailUrl}" alt="${item.name}"
           onerror="this.style.opacity=0.15" />
      <span class="thumb-label">${item.name}</span>`;
    el.addEventListener('click', () => {
      selectThumb(el);
      centerThumb(root, el);
      onSelect(item.sku);
      updateArrows();
    });
    root.appendChild(el);
    thumbs.push(el);
  });

  // Trailing spacer so the last item can center.
  const tailSpacer = document.createElement('div');
  tailSpacer.className = 'carousel-spacer';
  root.appendChild(tailSpacer);

  // Center the initially-active thumb (after layout settles).
  requestAnimationFrame(() => {
    const active = root.querySelector('.thumb.active') || thumbs[0];
    if (active) centerThumb(root, active, false);
  });

  // Arrow navigation: step selection to prev/next product.
  const leftBtn = document.getElementById('carousel-left');
  const rightBtn = document.getElementById('carousel-right');
  const step = (dir) => {
    const current = root.querySelector('.thumb.active');
    let i = thumbs.indexOf(current);
    if (i < 0) i = 0;
    const next = Math.max(0, Math.min(thumbs.length - 1, i + dir));
    if (next === i) return;
    const el = thumbs[next];
    selectThumb(el);
    centerThumb(root, el);
    onSelect(el.dataset.sku);
    updateArrows();
  };
  const updateArrows = () => {
    const current = root.querySelector('.thumb.active');
    const i = thumbs.indexOf(current);
    if (leftBtn) leftBtn.disabled = i <= 0;
    if (rightBtn) rightBtn.disabled = i >= thumbs.length - 1;
  };
  if (leftBtn) leftBtn.onclick = () => step(-1);
  if (rightBtn) rightBtn.onclick = () => step(1);
  updateArrows();

  function selectThumb(el) {
    root.querySelectorAll('.thumb').forEach(n => n.classList.remove('active'));
    el.classList.add('active');
  }
}

// Smoothly scroll the carousel so `el` is centered horizontally.
function centerThumb(root, el, smooth = true) {
  const target = el.offsetLeft - (root.clientWidth / 2) + (el.clientWidth / 2);
  root.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
}

export function setStatus(text, ready = false) {
  const el = document.getElementById('status');
  if (el) {
    el.textContent = text;
    el.classList.toggle('ready', ready);
  }
  // Let the embed bridge (src/sdk/bridge.js) forward status to the host page.
  window.dispatchEvent(new CustomEvent('pointai:status', { detail: { text, ready } }));
}

// Programmatically mark a SKU as the active thumbnail and centre it (used when
// the host page selects a product over postMessage). Does NOT call onSelect.
export function setActiveThumb(sku) {
  const root = document.getElementById('picker');
  if (!root) return;
  const el = root.querySelector(`.thumb[data-sku="${CSS.escape(sku)}"]`);
  if (!el) return;
  root.querySelectorAll('.thumb').forEach(n => n.classList.remove('active'));
  el.classList.add('active');
  centerThumb(root, el);
  const thumbs = [...root.querySelectorAll('.thumb')];
  const i = thumbs.indexOf(el);
  const leftBtn = document.getElementById('carousel-left');
  const rightBtn = document.getElementById('carousel-right');
  if (leftBtn) leftBtn.disabled = i <= 0;
  if (rightBtn) rightBtn.disabled = i >= thumbs.length - 1;
}

export function showOverlay({ title, message, buttonText, onClick }) {
  const overlay = document.getElementById('overlay');
  document.getElementById('overlay-title').textContent = title;
  document.getElementById('overlay-msg').textContent = message;
  const btn = document.getElementById('overlay-btn');
  btn.textContent = buttonText || 'OK';
  btn.onclick = () => { onClick?.(); };
  overlay.classList.add('show');
}

export function hideOverlay() {
  document.getElementById('overlay').classList.remove('show');
}

export function setFps(fps) {
  const el = document.getElementById('fps');
  if (el) el.textContent = `${Math.round(fps)} fps`;
}

// Update the on-screen PD badge. Pass a number (mm) or null to dim it.
let _pdSmooth = null;
export function setPD(pdMm, confidence) {
  const el = document.getElementById('pd-badge');
  const val = document.getElementById('pd-value');
  if (!el || !val) return;
  if (!Number.isFinite(pdMm)) { el.classList.add('dim'); val.textContent = '—'; return; }
  // Light smoothing so the number doesn't flicker frame-to-frame.
  _pdSmooth = (_pdSmooth == null) ? pdMm : _pdSmooth * 0.8 + pdMm * 0.2;
  val.textContent = Math.round(_pdSmooth);
  // Dim the badge when confidence is low (head turned / far), like a soft hint.
  el.classList.toggle('dim', Number.isFinite(confidence) && confidence < 0.6);
}

export function showLoader(text) {
  const el = document.getElementById('loader');
  if (!el) { console.warn('[loader] #loader element not found'); return; }
  const label = el.querySelector('.loader-text');
  if (label && text) label.textContent = text;
  el.classList.add('show');
  console.log('[loader] show:', text || '');
}

export function hideLoader() {
  const el = document.getElementById('loader');
  if (!el) return;
  el.classList.remove('show');
  console.log('[loader] hide');
}
